import { Button, Select, TextInput } from '@octano/global-ui';
import { useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col } from 'reactstrap';

import RemoteSearchSelect from '../../../../../../components/form/RemoteSearchSelect';
import { useSearchTeachers } from '../../../../../../hooks/remoteSearchSelect/useSearchTeachers';
import { useValidations } from '../../../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../../../locales/translations';
import { formatBaseOption } from '../../../../../../utils/selectFormat';
import { ModalityCodesEnum } from '../../../../../SectionsMaintainer/enums/modality-codes.enum';
import { useCoursesWithSectionsFormDataLoader } from '../Loaders/CoursesSectionsLoader';
import { TeacherRow } from './TeacherRow';
import { CourseWithSection } from '../types';

interface TeacherModalityConfig {
  showOnline: boolean;
  showPresential: boolean;
  onlineOnly: boolean;
  presentialOnly: boolean;
}

interface Props {
  course: CourseWithSection;
}

export function CreateSectionForm({ course }: Props) {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_ACADEMIC_OFFERS,
  );
  const { result } = useCoursesWithSectionsFormDataLoader();
  const formDataResult = result?.success ? result.data : null;

  const { msgValidations } = useValidations();
  const { control, setValue, getValues, watch } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'teachers',
  });

  const watchedTeachers = watch('teachers');

  const options = useMemo(() => {
    if (!formDataResult) {
      return {
        campusOptions: [],
        schedulesOptions: [],
        teachersOptions: [],
      };
    }
    return {
      campusOptions: (formDataResult.campuses ?? []).map(formatBaseOption),
      schedulesOptions: (
        formDataResult?.schedules.map((section) => {
          return {
            id: section.id,
            name: section.name,
          };
        }) ?? []
      ).map(formatBaseOption),
      teachersOptions: (
        formDataResult?.teachers?.map((teacher) => ({
          id: teacher.id,
          name: teacher.account?.fullName ?? '-',
        })) ?? []
      ).map(formatBaseOption),
    };
  }, [formDataResult]);

  // Determinar opciones de modalidad según el tipo de asignatura
  const modalityOptions: TeacherModalityConfig = useMemo(() => {
    const mode = course.modalityDeliveryMode;
    return {
      showOnline:
        mode === ModalityCodesEnum.SP ||
        mode === ModalityCodesEnum.OL ||
        mode === ModalityCodesEnum.OA,
      showPresential:
        mode === ModalityCodesEnum.SP || mode === ModalityCodesEnum.PR,
      onlineOnly:
        mode === ModalityCodesEnum.OL || mode === ModalityCodesEnum.OA,
      presentialOnly: mode === ModalityCodesEnum.PR,
    };
  }, [course.modalityDeliveryMode]);

  const handleAddTeacher = () => {
    append({
      teacher: null,
      isOnline: modalityOptions.onlineOnly,
      isPresential: modalityOptions.presentialOnly,
    });
  };

  const searchTeachers = useSearchTeachers();

  return (
    <>
      <Col xs={12} md={6} className="mb-3">
        <TextInput
          name="name"
          label={t('sections.modal.name')}
          control={control}
          rules={{ required: msgValidations.required }}
        />
      </Col>
      <Col xs={12} md={6} className="mb-3">
        <TextInput
          name="code"
          label={t('sections.modal.code')}
          control={control}
          rules={{ required: msgValidations.required }}
        />
      </Col>
      <Col xs={12} md={6} className="mb-3">
        <Select
          label={t('sections.modal.campus')}
          name="campus"
          options={options.campusOptions}
          control={control}
          rules={{ required: msgValidations.required }}
        />
      </Col>
      <Col xs={12} md={6} className="mb-3">
        <TextInput
          name="quota"
          label={t('sections.modal.quota')}
          control={control}
          formatter={(value) => value.replace(/\D/g, '')}
          rules={{
            required: msgValidations.required,
            min: {
              value: 1,
              message: t('form.completionDaysLimit.minError'),
            },
          }}
        />
      </Col>
      <Col xs={12} md={6} className="mb-3">
        <Select
          label={t('sections.modal.schedule')}
          name="schedule"
          options={options.schedulesOptions}
          control={control}
          rules={{ required: msgValidations.required }}
        />
      </Col>
      <Col xs={12}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <label className="form-label text-dark mb-0">
            {t('sections.modal.teachers')}
          </label>
          <Button
            text={t('sections.modal.addTeacher')}
            outlined
            size="sm"
            icon="plus"
            onClick={handleAddTeacher}
          />
        </div>

        {fields.map((field, index) => {
          const teacher = watchedTeachers?.[index];
          return (
            <div key={field.id} className="mb-3">
              <TeacherRow
                isOnline={teacher?.isOnline ?? false}
                isPresential={teacher?.isPresential ?? false}
                showOnline={modalityOptions.showOnline}
                showPresential={modalityOptions.showPresential}
                onToggleOnline={
                  !modalityOptions.onlineOnly || index === 0
                    ? () => {
                        const currentValue = getValues(
                          `teachers.${index}.isOnline`,
                        );
                        setValue(`teachers.${index}.isOnline`, !currentValue);
                      }
                    : undefined
                }
                onTogglePresential={
                  !modalityOptions.presentialOnly || index === 0
                    ? () => {
                        const currentValue = getValues(
                          `teachers.${index}.isPresential`,
                        );
                        setValue(
                          `teachers.${index}.isPresential`,
                          !currentValue,
                        );
                      }
                    : undefined
                }
                onDelete={fields.length > 1 ? () => remove(index) : undefined}
              >
                <RemoteSearchSelect
                  name={`teachers.${index}.teacher`}
                  label={t('sections.modal.teacher')}
                  control={control}
                  rules={{ required: msgValidations.required }}
                  filterOptions={searchTeachers}
                  placeholder={t('sections.modal.searchTeacher')}
                />
              </TeacherRow>
            </div>
          );
        })}
      </Col>
    </>
  );
}
